// The batch spec sub-form: everything a `BatchPreviewRequest` carries —
// Target (the fixed axes), Personas + persona-owned sweeps, Scenario +
// scenario-owned sweeps, the reproducible sample, and simulation options.
// Sections follow the axis registry's owner grouping (docs/14 §8).
//
// This is the one place those fields are authored. The ad-hoc launch form
// wraps it with a Batch name + template provenance + the launch receipt; the
// Template authoring route wraps it with a display name + schedule. Neither
// re-implements a field, so a saved Template and an ad-hoc launch can never
// offer different inputs for the same spec (docs/14 §5: there is no second
// launch path).
//
// Props-pure: no queries, no mutations. Options come from `useLaunchOptions`.
import { docUrl } from "../../lib/docLinks";
import {
  Button,
  Checkbox,
  HelpTip,
  Inline,
  MultiSelect,
  Section,
  Select,
  ShellIcon,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import {
  randomSampleSeed,
  sampleSeedError,
  type SimulationLaunchState,
  type VariableDeclaration,
} from "./simulationLaunchState";
import type { LaunchOption } from "./useLaunchOptions";
import { VariableSweepEditor } from "./VariableSweepEditor";

export interface BatchSpecFieldsProps {
  advancedDisclosure?: boolean;
  agentId: string;
  connectionOptions: LaunchOption[];
  onChange: (state: SimulationLaunchState) => void;
  personaOptions: LaunchOption[];
  scenarioSetOptions: LaunchOption[];
  state: SimulationLaunchState;
  templateAuthoring?: boolean;
  variables: VariableDeclaration[];
  versionOptions: LaunchOption[];
}

export function optionItems(options: LaunchOption[]) {
  return options.map((option) => (
    <option key={option.id} value={option.id}>
      {option.label}
    </option>
  ));
}

/** Resolve a stored id to its human label for a summary row. */
export function labelFor(
  options: LaunchOption[],
  id: string,
  fallback: string,
): string {
  if (id.length === 0) {
    return fallback;
  }
  return options.find((option) => option.id === id)?.label ?? fallback;
}

export function BatchSpecFields({
  advancedDisclosure = false,
  agentId,
  connectionOptions,
  onChange,
  personaOptions,
  scenarioSetOptions,
  state,
  templateAuthoring = false,
  variables,
  versionOptions,
}: BatchSpecFieldsProps) {
  const patch = (partial: Partial<SimulationLaunchState>) => {
    onChange({ ...state, ...partial });
  };

  const variableSweepFields = (
    <Section
      help={
        <HelpTip
          docHref={docUrl("guides/use-variables-in-simulations")}
          docLabel="Use variables in simulations"
          label="About variable sweeps"
        >
          {
            "Each override set adds a variation using the highest-priority values. Blank fields keep the value from the Agent, Persona, Scenario, or row. Two rows and three override sets produce six cases per Scenario and Persona pairing. Declare variables in a new tab without losing this form."
          }
        </HelpTip>
      }
      heading="Variable sweep"
    >
      <VariableSweepEditor
        agentId={agentId}
        drafts={state.sweepVariables}
        variables={variables}
        onChange={(sweepVariables) => {
          patch({ sweepVariables });
        }}
      />
    </Section>
  );

  const sampleFields = (
    <Section
      actions={
        <Checkbox
          checked={state.sampleEnabled}
          label="Sample a subset of the expansion"
          onChange={(event) => {
            const sampleEnabled = event.currentTarget.checked;
            patch({
              sampleEnabled,
              sampleSeed:
                sampleEnabled && state.sampleSeed.trim().length === 0
                  ? String(randomSampleSeed())
                  : state.sampleSeed,
            });
          }}
        />
      }
      heading="Sample"
      help={
        <HelpTip
          docHref={docUrl("guides/run-a-simulated-test")}
          docLabel="Run a simulated test"
          label="About sampling"
        >
          {"Sampling picks N cells from the full expansion. Each Batch " +
            "records its seed and population fingerprint, so Rerun " +
            "reproduces that Batch’s sample exactly."}
        </HelpTip>
      }
    >
      {templateAuthoring ? (
        <Text variant="muted">
          Every scheduled run chooses a fresh sample seed. Each Batch records
          its seed and population fingerprint; Rerun reproduces that Batch’s
          sample exactly.
        </Text>
      ) : null}
      {state.sampleEnabled ? (
        <Inline align="start" gap={3}>
          <TextInput
            inputMode="numeric"
            label="Sample size"
            required
            value={state.sampleSize}
            onChange={(event) => {
              patch({ sampleSize: event.currentTarget.value });
            }}
          />
          {templateAuthoring ? null : (
            <>
              <TextInput
                error={sampleSeedError(state.sampleSeed)}
                inputMode="numeric"
                label="Seed"
                required
                value={state.sampleSeed}
                onChange={(event) => {
                  patch({ sampleSeed: event.currentTarget.value });
                }}
              />
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => {
                  patch({ sampleSeed: String(randomSampleSeed()) });
                }}
              >
                Generate
              </Button>
            </>
          )}
        </Inline>
      ) : null}
    </Section>
  );

  const simulationOptionFields = (
    <Section
      heading="Simulation options"
      help={
        <HelpTip
          docHref={docUrl("guides/run-a-simulated-test")}
          docLabel="Run a simulated test"
          label="About simulation options"
        >
          {
            "Leave max duration blank to use the deployment default. Batch concurrency is a requested ceiling; platform capacity can lower it."
          }
        </HelpTip>
      }
    >
      <Inline align="start" gap={3}>
        <TextInput
          inputMode="numeric"
          label="Max duration (seconds)"
          value={state.maxDurationSeconds}
          onChange={(event) => {
            patch({ maxDurationSeconds: event.currentTarget.value });
          }}
        />
        <TextInput
          inputMode="numeric"
          label="Batch concurrency max"
          value={state.batchConcurrencyMax}
          onChange={(event) => {
            patch({ batchConcurrencyMax: event.currentTarget.value });
          }}
        />
      </Inline>
    </Section>
  );

  return (
    <>
      <Section
        heading="Target"
        help={
          <HelpTip
            docHref={docUrl("concepts/simulated-testing")}
            docLabel="Simulated testing"
            label="About the target"
          >
            {
              "One Agent Version and one Connection apply to every conversation in the Batch, so results compare cleanly against another Version later."
            }
          </HelpTip>
        }
      >
        <Inline align="start" gap={3}>
          <Select
            label="Version"
            value={state.agentVersionId}
            onChange={(event) => {
              patch({ agentVersionId: event.currentTarget.value });
            }}
          >
            <option value="">Select version</option>
            {optionItems(versionOptions)}
          </Select>
          <Select
            label="Connection"
            value={state.connectionId}
            onChange={(event) => {
              patch({ connectionId: event.currentTarget.value });
            }}
          >
            <option value="">Select connection</option>
            {optionItems(connectionOptions)}
          </Select>
        </Inline>
      </Section>

      <Section
        heading="Personas"
        help={
          <HelpTip
            docHref={docUrl("guides/author-a-persona")}
            docLabel="Author a Persona"
            label="About Personas and sweeps"
          >
            {
              "A Persona is the simulated caller. Personas cross with the Scenario Set, and each sweep value below multiplies that again:\n\n- 2 Personas x 3 Scenarios = 6 runs.\n- Adding `en, es` to the language sweep makes it 12.\n\nThe launch summary shows the real count before you launch."
            }
          </HelpTip>
        }
      >
        <Stack gap={3}>
          <MultiSelect
            emptyLabel="No personas for this agent yet."
            label="Personas"
            options={personaOptions.map((option) => ({
              label: option.label,
              value: option.id,
            }))}
            value={state.personaIds}
            onChange={(personaIds) => {
              patch({ personaIds });
            }}
          />
          {/* Language and accent sweeps remain supported by the launch spec,
              but are intentionally hidden while the launch form is decluttered.
              Use multiple Personas for caller variation in the current UI. */}
        </Stack>
      </Section>

      <Section
        heading="Scenario"
        help={
          <HelpTip
            docHref={docUrl("concepts/simulated-testing")}
            docLabel="Simulated testing"
            label="About Scenarios"
          >
            {
              "A Scenario is the situation the caller follows; a Scenario Set is an ordered group of them. Background and variable sweeps vary the situation, not the caller."
            }
          </HelpTip>
        }
      >
        <Stack gap={3}>
          <Select
            label="Scenario set"
            value={state.scenarioSetId}
            onChange={(event) => {
              patch({ scenarioSetId: event.currentTarget.value });
            }}
          >
            <option value="">Select scenario set</option>
            {optionItems(scenarioSetOptions)}
          </Select>
          {/* Background sweeps remain supported by the launch spec, but are
              intentionally hidden while the launch form is decluttered. Use
              multiple Scenario Sets for scenario variation in the current UI. */}
          {advancedDisclosure ? null : variableSweepFields}
        </Stack>
      </Section>

      {advancedDisclosure ? (
        <details className="marker-simulation-advanced">
          <summary className="marker-simulation-advanced__summary">
            <span>Advanced</span>
            <span
              aria-hidden="true"
              className="marker-simulation-advanced__marker"
            >
              <ShellIcon name="chevronDown" />
            </span>
          </summary>
          <Stack className="marker-simulation-advanced__body" gap={6}>
            {variableSweepFields}
            {sampleFields}
            {simulationOptionFields}
          </Stack>
        </details>
      ) : (
        <>
          {sampleFields}
          {simulationOptionFields}
        </>
      )}
    </>
  );
}
